import { useState } from 'react'
import { observer } from 'mobx-react'
import { useShowDialog } from 'App'
import type { ExistingVehicle } from 'models/Vehicles/VehicleModel'
import { ProfileUpdateRequestStatus } from 'models/ota/ProfileUpdateRequestModel'
import type { ProfilesStoreType, ProfileUpdateRequestsStoreType } from 'stores/RootStoreModel'
import { ModuleVersionsContainer } from './ModuleVersionsContainer'
import { ProfileUpdatePopup } from './ProfileUpdatePopup'
import { showNotificationForObject } from 'utils/NotificationUtils'
import { NotificationLevel } from 'stores/NotificationStore'
import { getAvailableProfiles, getInstalledProfileIds, getLatestProfileId } from './profileUpdateUtils'

const getSubTitle = (vehicles: ReadonlyArray<ExistingVehicle>) => {
    const [singleVehicle] = vehicles
    return vehicles.length === 1 && singleVehicle !== undefined
        ? `Vehicle ${singleVehicle.id}`
        : `${vehicles.length} vehicles selected`
}

interface ProfileUpdatePopupContainerProps {
    vehicles: ReadonlyArray<ExistingVehicle>
    profilesStore: ProfilesStoreType
    profileUpdateRequestsStore: ProfileUpdateRequestsStoreType
    // Called once the requests were sent, also when some of them failed
    onRequested?: (isAllRequested: boolean) => void
}

// Prepares the vehicles' profiles for ProfileUpdatePopup, and sends the update requests
const ProfileUpdatePopupContainer = observer(({ vehicles, profilesStore, profileUpdateRequestsStore, onRequested }: ProfileUpdatePopupContainerProps) => {
    const showDialog = useShowDialog()
    const hideDialog = () => { showDialog(null) }
    const [isRequesting, setIsRequesting] = useState(false)

    const installedProfileIds = getInstalledProfileIds(vehicles)
    const hasMixedInstalledProfiles = installedProfileIds.size > 1
    const [installedProfileId = null] = installedProfileIds
    // Only when all the vehicles have the same known installed profile
    const installedProfile = !hasMixedInstalledProfiles && installedProfileId !== null ? profilesStore.getProfile(installedProfileId) : undefined

    const onUpdate = async (profileId: string) => {
        setIsRequesting(true)
        const isAllRequested = await profileUpdateRequestsStore.createForVehicles(vehicles.map(vehicle => vehicle.id), profileId)
        onRequested?.(isAllRequested)
        hideDialog()
    }

    return <ProfileUpdatePopup {...{
        subTitle: getSubTitle(vehicles),
        profilesError: profilesStore.fetchStatus.isFailure ? profilesStore.fetchStatus.error : undefined,
        onRetryProfiles: () => profilesStore.fetchProfiles(),
        installedProfile,
        hasMixedInstalledProfiles,
        availableProfiles: getAvailableProfiles(profilesStore.profiles, installedProfile?.id ?? null),
        latestProfileId: getLatestProfileId(profilesStore.profiles),
        // The available versions show the change of each module from the installed profile
        renderModuleVersions: (profileId: string) => <ModuleVersionsContainer {...{
            profilesStore,
            profileId,
            installedProfileId: profileId !== installedProfile?.id ? installedProfile?.id : undefined,
        }} />,
        isRequesting,
        onUpdate: (profileId: string) => { void onUpdate(profileId) },
        onClose: hideDialog,
    }} />
})

interface ShowProfileUpdateDialogParams extends ProfileUpdatePopupContainerProps {
    showDialog: ReturnType<typeof useShowDialog>
}

// Opens the popup for requesting a profile update of one vehicle or more.
// Not opened for vehicles with different installed profiles, as they can't be updated together,
// nor when any of the vehicles is already being updated (an active request past the requested status).
export function showProfileUpdateDialog({ showDialog, ...popupProps }: ShowProfileUpdateDialogParams) {
    const { vehicles, profileUpdateRequestsStore } = popupProps
    if (getInstalledProfileIds(vehicles).size > 1) {
        showNotificationForObject(`Can't update vehicles with different installed profiles together`, NotificationLevel.Error)
        return
    }
    const updatingVehicleIds = vehicles
        .filter(vehicle => {
            const activeRequest = profileUpdateRequestsStore.getActiveRequest(vehicle.id)
            return activeRequest !== undefined && activeRequest.status !== ProfileUpdateRequestStatus.Requested
        })
        .map(vehicle => vehicle.id)
    if (updatingVehicleIds.length > 0) {
        showNotificationForObject(`Can't update vehicles that are already being updated: ${updatingVehicleIds.join(', ')}`, NotificationLevel.Error)
        return
    }
    showDialog({
        noPadding: true,
        content: <ProfileUpdatePopupContainer {...popupProps} />,
    })
}
